import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import BreadCrumbComp from "../../../components/breadCrumb";
import LaddaButton from "../../../components/buttons/laddaButton";
import VerificationModal from "../../../components/modals/verificationModal";
import SimpleSelect from "../../../components/selects/simpleSelect";
import FormTextField from "../../../components/textField/formTextField";
import SimpleTable from "../../../components/tables/simpleTable";
import ToastNotification from "../../../components/toastNotification";
import { icons } from "../../../constants";
import { canManageWalletProducts } from "../../../constants/walletRoles";
import "../styles.css";
import { walletProductColumns } from "./tableColumns";
import {
  deleteWalletProductDATA,
  getWalletProductLinesDATA,
  getWalletProductsDATA,
  saveWalletProductDATA
} from "./walletProductsCallApi";

const isPartialCodeOptions = [
  { name: "No", value: 0 },
  { name: "Si", value: 1 }
];

const productFormDefaultValues = {
  code: "",
  name: "",
  lineId: "",
  isPartialCode: 0
};

const filterFormDefaultValues = {
  search: "",
  lineFilter: "null"
};

const normalizeLineOptions = (options = []) =>
  (options || []).map((option) => ({
    name: option.name,
    value: Number(option.value)
  }));

const ProductSaveValidationModal = ({
  open,
  onClose,
  onConfirm,
  pendingProduct,
  isEditing
}) => {
  if (!open || !pendingProduct) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" component="span" color="primary.main">
          Validar producto
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Antes de {isEditing ? "actualizar" : "crear"} el producto, confirma que el código
          corresponda al producto de ventas y no al COM-NUMERO-VT de una factura.
        </Typography>

        <div className="info-card" style={{ marginBottom: "16px" }}>
          <img
            className="btn-icon-32 mr-10"
            src={icons.info_blue}
            alt="icon"
          />
          <span>
            Ejemplo correcto de producto: <strong>10001000007</strong>. Ejemplo que no debe
            guardarse como producto: <strong>F-004-00000015924-001</strong>.
          </span>
        </div>

        <div style={{ display: "grid", gap: "8px", fontSize: "13px" }}>
          <span>
            <strong>Código:</strong> {pendingProduct.code}
          </span>
          <span>
            <strong>Producto:</strong> {pendingProduct.name}
          </span>
          <span>
            <strong>Línea de negocio:</strong> {pendingProduct.lineName}
          </span>
          <span>
            <strong>Código parcial:</strong>{" "}
            {pendingProduct.isPartialCode === 1
              ? "Sí, representa una familia o referencia base."
              : "No, representa una referencia exacta."}
          </span>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <LaddaButton
          color="6A6AD8"
          backgroundColor={"white"}
          onClick={onClose}
        >
          <div className="HContainer align-center">
            <p className="button_text">Cancelar</p>
          </div>
        </LaddaButton>
        <LaddaButton variant="contained" onClick={onConfirm}>
          <div className="HContainer align-center">
            <p className="button_text">
              {isEditing ? "Confirmar actualización" : "Confirmar creación"}
            </p>
          </div>
        </LaddaButton>
      </DialogActions>
    </Dialog>
  );
};

const WalletProducts = () => {
  const userData = useSelector((state) => state.userData.userData);
  const canManageProducts = canManageWalletProducts(userData?.roleList);

  const [lineOptions, setLineOptions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [loadingTable, setLoadingTable] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productPendingDelete, setProductPendingDelete] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    lineId: null
  });
  const [showToast, setShowToast] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");
  const [pendingProductSave, setPendingProductSave] = useState(null);
  const [openProductValidationModal, setOpenProductValidationModal] = useState(false);

  const {
    control: productControl,
    handleSubmit: handleProductSubmit,
    reset: resetProductForm
  } = useForm({
    defaultValues: productFormDefaultValues
  });

  const {
    control: filterControl,
    handleSubmit: handleFilterSubmit,
    reset: resetFilterForm
  } = useForm({
    defaultValues: filterFormDefaultValues
  });

  const rawLineOptions = useMemo(
    () => normalizeLineOptions(lineOptions),
    [lineOptions]
  );

  const productLineOptions = useMemo(
    () => [{ name: "Seleccione", value: "" }, ...rawLineOptions],
    [rawLineOptions]
  );

  const filterLineOptions = useMemo(
    () => [{ name: "Todas", value: "null" }, ...rawLineOptions],
    [rawLineOptions]
  );

  const columns = useMemo(
    () =>
      canManageProducts
        ? walletProductColumns
        : walletProductColumns.filter((col) => col.field !== "actions"),
    [canManageProducts]
  );

  const showToastMessage = useCallback((severity, message) => {
    setToastSeverity(severity);
    setToastMessage(message);
    setShowToast(true);
  }, []);

  const refreshProducts = () => setRefreshKey((value) => value + 1);

  useEffect(() => {
    getWalletProductLinesDATA(setLineOptions, userData?.id);
  }, []);

  useEffect(() => {
    getWalletProductsDATA({
      page: currentPage,
      pageSize,
      filters: appliedFilters,
      setData: setTableData,
      setTotal: setTotalRecords,
      setPage: setCurrentPage,
      setLoading: setLoadingTable,
      onError: (message) => showToastMessage("error", message),
      userId: userData?.id
    });
  }, [appliedFilters, currentPage, pageSize, refreshKey, showToastMessage]);

  const handlePageChange = (newPage, newPageSize) => {
    setCurrentPage(newPage);
    if (newPageSize) setPageSize(newPageSize);
  };

  const clearProductForm = () => {
    setSelectedProduct(null);
    setPendingProductSave(null);
    setOpenProductValidationModal(false);
    resetProductForm(productFormDefaultValues);
  };

  const handleEditProduct = (row) => {
    setSelectedProduct(row);
    resetProductForm({
      code: row.code != null ? String(row.code) : "",
      name: row.name || "",
      lineId: row.line_id != null ? Number(row.line_id) : "",
      isPartialCode: Number(row.is_partial_code) === 1 ? 1 : 0
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRequestDelete = (row) => {
    setProductPendingDelete(row);
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setProductPendingDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!productPendingDelete?.id) return;

    await deleteWalletProductDATA({
      productId: productPendingDelete.id,
      userId: userData?.id,
      onSuccess: (data) => {
        showToastMessage(
          "success",
          data?.message || "Producto eliminado correctamente"
        );
        closeDeleteModal();
        if (selectedProduct?.id === productPendingDelete.id) {
          clearProductForm();
        }
        refreshProducts();
      },
      onError: (message) => showToastMessage("error", message)
    });
  };

  const handleSaveProduct = async (formData) => {
    if (!canManageProducts) return;

    const code = Number(formData.code);
    const lineId = Number(formData.lineId);
    const name = String(formData.name || "").trim();
    const isPartialCode = Number(formData.isPartialCode) === 1 ? 1 : 0;

    if (!Number.isInteger(code) || code <= 0) {
      showToastMessage("error", "Ingresa un codigo de producto valido.");
      return;
    }

    if (!name) {
      showToastMessage("error", "Ingresa el nombre del producto.");
      return;
    }

    if (!Number.isInteger(lineId) || lineId <= 0) {
      showToastMessage("error", "Selecciona una linea de negocio.");
      return;
    }

    const selectedLine = productLineOptions.find(
      (line) => Number(line.value) === lineId
    );
    setPendingProductSave({
      productId: selectedProduct?.id || null,
      code,
      name,
      lineId,
      lineName: selectedLine?.name || "Sin linea seleccionada",
      isPartialCode
    });
    setOpenProductValidationModal(true);
  };

  const handleConfirmSaveProduct = async () => {
    if (!pendingProductSave) return;

    await saveWalletProductDATA({
      productId: pendingProductSave.productId,
      userId: userData?.id,
      payload: {
        code: pendingProductSave.code,
        name: pendingProductSave.name,
        lineId: pendingProductSave.lineId,
        isPartialCode: pendingProductSave.isPartialCode
      },
      onSuccess: (data) => {
        showToastMessage(
          "success",
          data?.message || "Producto guardado correctamente"
        );
        setPendingProductSave(null);
        setOpenProductValidationModal(false);
        clearProductForm();
        refreshProducts();
      },
      onError: (message) => showToastMessage("error", message)
    });
  };

  const handleCloseProductValidationModal = () => {
    setOpenProductValidationModal(false);
    setPendingProductSave(null);
  };

  const handleApplyFilters = (formData) => {
    setAppliedFilters({
      search: String(formData.search || "").trim(),
      lineId:
        formData.lineFilter && formData.lineFilter !== "null"
          ? formData.lineFilter
          : null
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    resetFilterForm(filterFormDefaultValues);
    setAppliedFilters({ search: "", lineId: null });
    setCurrentPage(1);
  };

  const tableButtons = [
    {
      type: "icon",
      icon: icons.editBlue,
      onClick: handleEditProduct
    },
    {
      type: "icon",
      icon: icons.redTrash,
      onClick: handleRequestDelete
    }
  ];

  return (
    <>
      <ToastNotification
        severity={toastSeverity}
        summary={toastMessage}
        detail=""
        position="top-center"
        showToast={showToast}
        setShowToast={setShowToast}
      />

      <BreadCrumbComp />
      <div className="searchTitle">
        <h1>PRODUCTOS CARTERA</h1>
      </div>

      {canManageProducts && (
        <div className="table-container">
          <h1 className="title-bio-details-chard">
            {selectedProduct ? "Editar producto" : "Crear producto"}
          </h1>

          <div className="info-card" style={{ marginBottom: "16px" }}>
            <img
              className="btn-icon-32 mr-10"
              src={icons.info_blue}
              alt="icon"
            />
            <span style={{ display: "grid", gap: "6px" }}>
              <span>
                <strong>Código:</strong> debe ser numérico, por ejemplo{" "}
                <strong>10001000007</strong>. No ingreses documentos de factura como{" "}
                <strong>F-004-00000015924-001</strong>; ese dato corresponde al COM-NUMERO-VT. Si
                necesitas identificar la factura en recaudo, usa el número central{" "}
                <strong>00000015924</strong>, no como código de producto.
              </span>
              <span>
                <strong>Producto:</strong> nombre descriptivo que viene en ventas.
              </span>
              <span>
                <strong>Línea de negocio:</strong> línea a la que se asignarán las ventas de ese
                producto.
              </span>
              <span>
                <strong>Código parcial:</strong> usa <strong>Sí</strong> cuando el código represente
                una familia o referencia base que agrupa varias referencias, por ejemplo una línea de
                equipos con seriales diferentes bajo la misma base. Usa <strong>No</strong> cuando el
                código identifica una referencia exacta, por ejemplo{" "}
                <strong>90001000034</strong> para un producto específico.
              </span>
            </span>
          </div>

          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={2}>
              <FormTextField
                name="code"
                control={productControl}
                label="Codigo:"
                onlyNumbers
                maxLength={20}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormTextField
                name="name"
                control={productControl}
                label="Producto:"
                maxLength={255}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <p className="blueLabelForm">Linea de negocio:</p>
              <SimpleSelect
                margin={"0"}
                name="lineId"
                control={productControl}
                options={productLineOptions}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <p className="blueLabelForm">Codigo parcial:</p>
              <SimpleSelect
                margin={"0"}
                name="isPartialCode"
                control={productControl}
                options={isPartialCodeOptions}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <div
                className="HContainer align-center j-end"
                style={{ flexWrap: "wrap", gap: "8px" }}
              >
                <LaddaButton
                  variant="contained"
                  onClick={handleProductSubmit(handleSaveProduct)}
                  margin={"0"}
                >
                  <div className="HContainer align-center">
                    <img
                      className="btn-icon mr-10"
                      src={selectedProduct ? icons.editWhite : icons.plusIcon}
                      alt="icon"
                    />
                    <p className="button_text">
                      {selectedProduct ? "Actualizar" : "Crear"}
                    </p>
                  </div>
                </LaddaButton>
                {selectedProduct && (
                  <LaddaButton
                    variant="contained"
                    backgroundColor="#ffffff"
                    color="#6A6AD8"
                    borderColor="#6A6AD8"
                    onClick={clearProductForm}
                    margin={"0"}
                  >
                    <p className="button_text">Cancelar</p>
                  </LaddaButton>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      )}

      <div className="table-container">
        <h1 className="title-bio-details-chard">Catalogo de productos</h1>

        <Grid container spacing={1} alignItems="flex-end">
          <Grid item xs={12} md={5}>
            <FormTextField
              name="search"
              control={filterControl}
              label="Buscar:"
              maxLength={120}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <p className="blueLabelForm">Linea de negocio:</p>
            <SimpleSelect
              margin={"0"}
              name="lineFilter"
              control={filterControl}
              options={filterLineOptions}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <div className="IHContainer j-end" style={{ alignItems: "flex-end" }}>
              <LaddaButton
                variant="contained"
                onClick={handleFilterSubmit(handleApplyFilters)}
                margin={"0 0 0 10px"}
                width={"52px"}
              >
                <img className="btn-icon" src={icons.filterWhite} alt="icon" />
              </LaddaButton>
              <LaddaButton
                variant="contained"
                onClick={handleClearFilters}
                margin={"0 0 0 10px"}
              >
                <img
                  className="btn-icon"
                  src={icons.clearFilterWhite}
                  alt="icon"
                />
              </LaddaButton>
            </div>
          </Grid>
        </Grid>

        <br />

        <SimpleTable
          data={tableData}
          columns={columns}
          buttons={canManageProducts ? tableButtons : []}
          isPaginated={true}
          serverPagination={true}
          totalRecords={totalRecords}
          rowsPerPage={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          showItemCount={true}
          emptyMessage={
            loadingTable
              ? "Consultando productos..."
              : "No se han encontrado productos"
          }
          scrollHeight="1000px"
        />
      </div>

      <VerificationModal
        open={openDeleteModal}
        handleClose={closeDeleteModal}
        iconSeverity="danger"
        alertText={
          productPendingDelete
            ? `Esta accion eliminara el producto ${productPendingDelete.code} - ${productPendingDelete.name}.`
            : "Esta accion eliminara el producto seleccionado."
        }
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteModal}
      />
      <ProductSaveValidationModal
        open={openProductValidationModal}
        onClose={handleCloseProductValidationModal}
        onConfirm={handleConfirmSaveProduct}
        pendingProduct={pendingProductSave}
        isEditing={Boolean(pendingProductSave?.productId)}
      />
    </>
  );
};

export default WalletProducts;
